import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CreditCard, LogOut, Map, Shield, Trash2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { CATALOG_SITUATIONS } from "@/data/catalogScenarios";
import { findCatalogSituation } from "@/lib/catalog";
import { parseSessionAnalysis } from "@/lib/sessionAnalysis";
import { signOutUser } from "@/lib/signOut";
import { useProgress } from "@/hooks/useProgress";
import { useToast } from "@/hooks/use-toast";
import { useSubscription } from "@/contexts/SubscriptionContext";
import { AppCanvas } from "@/components/app/AppCanvas";
import { ProgressChart } from "@/components/progress/ProgressChart";
import { EmptyState } from "@/components/ui/empty-state";
import { ListCard, ListRow } from "@/components/ui/list-card";
import { Skeleton } from "@/components/ui/skeleton";

type HistoryRow = {
  id: string;
  catalog_slug: string | null;
  created_at: string | null;
  analysis: unknown;
};

const ProgressPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { isPaid } = useSubscription();
  const { progress, loading: progressLoading } = useProgress();
  const [rows, setRows] = useState<HistoryRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [signingOut, setSigningOut] = useState(false);
  const signingOutRef = useRef(false);

  const handleLogout = async () => {
    if (signingOutRef.current) return;
    signingOutRef.current = true;
    setSigningOut(true);
    const { error } = await signOutUser();
    if (error) {
      signingOutRef.current = false;
      setSigningOut(false);
      toast({
        title: "Erro ao sair",
        description: error.message,
        variant: "destructive",
      });
      return;
    }
    navigate("/auth");
  };

  useEffect(() => {
    const load = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setRows([]);
        setLoading(false);
        return;
      }
      const { data } = await supabase
        .from("conversation_sessions")
        .select("id, catalog_slug, created_at, analysis")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false })
        .limit(20);
      setRows((data || []) as HistoryRow[]);
      setLoading(false);
    };
    void load();
  }, []);

  return (
    <AppCanvas className="space-y-8">
      <div>
        <h1 className="text-h1 text-neutral-900">Progresso</h1>
        <p className="mt-1 text-body text-neutral-500">As últimas práticas e o feedback de cada uma.</p>
      </div>

      {!progressLoading && progress && <ProgressChart progress={progress} />}

      <section className="space-y-3">
        <h2 className="text-h3 text-neutral-900">Histórico</h2>
        {loading ? (
          <Skeleton className="h-24 w-full" />
        ) : rows.length === 0 ? (
          <EmptyState
            icon={Map}
            title="Seu mapa aparece aqui"
            body="Faça o primeiro ensaio e veja onde você já se vira."
            actionLabel="Começar o ensaio"
            to="/app"
          />
        ) : (
          <ListCard>
            {rows.map((row) => {
              const situation = findCatalogSituation(CATALOG_SITUATIONS, row.catalog_slug ?? undefined);
              const analysis = parseSessionAnalysis(row.analysis);
              const when = row.created_at ? new Date(row.created_at).toLocaleString("pt-BR") : "";
              return (
                <ListRow
                  key={row.id}
                  to={`/app/review/${row.id}`}
                  title={situation?.title || "Prática livre"}
                  subtitle={`${when}${analysis?.overall != null ? ` · ${analysis.overall} pts` : ""}`}
                  detail={analysis?.summary ?? undefined}
                />
              );
            })}
          </ListCard>
        )}
      </section>

      <section className="space-y-3">
        <h2 className="text-h3 text-neutral-900">Conta</h2>
        <ListCard>
          <ListRow icon={CreditCard} title={isPaid ? "Gerenciar assinatura" : "Assinar"} to="/app/upgrade" />
          <ListRow icon={Shield} title="Privacidade" to="/privacidade" />
          <ListRow icon={Trash2} title="Excluir conta" to="/excluir-conta" />
          <ListRow
            icon={LogOut}
            title={signingOut ? "Saindo…" : "Sair"}
            destructive
            testID="sign-out"
            onClick={() => void handleLogout()}
          />
        </ListCard>
      </section>
    </AppCanvas>
  );
};

export default ProgressPage;
